import { DndContext } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext } from '@dnd-kit/sortable';
import { wrap } from '@repo/di-ez';
import type { GridApi, GridColDef, GridColumnVisibilityModel } from '@repo/go-table';
import { Button, Checkbox, Field, InputNumber, Scrollbar } from '@repo/go-ui';
import { hasValue } from '@repo/utils';
import { useCallback, useMemo } from 'react';
import type { MutableRefObject, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { DisplaySettingItem } from '../../components/DisplaySettingItem';
import { MAX_NUMBER_DECIMAL_PLACES, MIN_NUMBER_DECIMAL_PLACES } from '../../consts';
import { MIN_VISIBLE_COLUMNS, SERVICE_COLUMNS, type DisplayManager } from '../trajectory-table/display-manager';
import { getTrajectoryColumnHeaderTKey } from '../trajectory-table/utils/get-column-header-t-key';

import styles from './trajectory-table-settings.module.scss';

type Props = {
  displayManager: DisplayManager;
  columns: GridColDef[];
  tableApi: MutableRefObject<GridApi>;
};

const HIDDEN_IN_SETTINGS_SIDEBAR_COLUMNS = [...SERVICE_COLUMNS];

export const TrajectoryTableSettings = wrap<Props>(function TrajectoryTableSettings({
  displayManager,
  columns,
  tableApi,
}) {
  const { t } = useTranslation();
  const useShortColumnHeaders = displayManager.useShortColumnHeaders;
  const numberDecimalPlaces = displayManager.numberDecimalPlaces;

  const { leftPinned, free, rightPinned, orderedColumns } = useMemo(() => {
    const { left, right } = displayManager.settings.pinnedColumns ?? {};

    const { leftPinned, free, rightPinned } = columns.reduce(
      (
        acc: {
          leftPinned: GridColDef[];
          free: GridColDef[];
          rightPinned: GridColDef[];
        },
        col,
      ) => {
        if (left?.includes(col.field)) {
          acc.leftPinned.push(col);
          return acc;
        }

        if (right?.includes(col.field)) {
          acc.rightPinned.push(col);
          return acc;
        }

        acc.free.push(col);

        return acc;
      },
      {
        leftPinned: [],
        free: [],
        rightPinned: [],
      },
    );

    const columnsOrderModel = displayManager.settings.columnsOrderModel;

    if (columnsOrderModel?.length) {
      free.sort((a, b) => {
        const c1index = columnsOrderModel.indexOf(a.field);
        const c2index = columnsOrderModel.indexOf(b.field);

        if (c1index < 0 && c2index < 0) {
          return 0;
        }

        if (c1index < 0) {
          return -1;
        }

        if (c2index < 0) {
          return 1;
        }

        return c1index - c2index;
      });
    }

    return { leftPinned, free, rightPinned, orderedColumns: [...leftPinned, ...free, ...rightPinned] };
  }, [columns, displayManager.settings.columnsOrderModel, displayManager.settings.pinnedColumns]);

  const handleColumnVisibilityChange = (column: string, value: boolean) => {
    const { settings } = displayManager;

    const newVisibilityModel: GridColumnVisibilityModel = {
      ...settings.columnsVisibility,
      [column]: value,
    };

    displayManager.onColumnVisibilityChange(newVisibilityModel);
  };

  const handleUnpinClick = (field: string) => {
    tableApi.current.unpinColumn(field);
  };

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { settings } = displayManager;

      const savedColumnsOrderModel = settings?.columnsOrderModel ?? [];
      const columnsOrderModel = [...columns.map((c) => c.field)].sort((a, b) => {
        const c1index = savedColumnsOrderModel.indexOf(a);
        const c2index = savedColumnsOrderModel.indexOf(b);

        if (c1index < 0 && c2index < 0) {
          return 0;
        }

        if (c1index < 0) {
          return -1;
        }

        if (c2index < 0) {
          return 1;
        }

        return c1index - c2index;
      });

      const activeField = event.active.id;
      const overField = event.over?.id;

      if (!hasValue(overField) || !hasValue(activeField)) {
        return;
      }

      const newIndex = columnsOrderModel.findIndex((item) => item === overField);
      const oldIndex = columnsOrderModel.findIndex((item) => item === activeField);

      const reorderedColumns = arrayMove(columnsOrderModel, oldIndex, newIndex);

      displayManager.onColumnsOrderModelChange(reorderedColumns);
    },
    [columns, displayManager],
  );

  const checkIsColumnVisibilityDisabled = (columnField: string): boolean => {
    const { settings } = displayManager;

    if (!settings.columnsOrderModel) return false;

    const visibleColumns = settings.columnsVisibility;

    if (!visibleColumns) return false;

    const isColumnVisible = visibleColumns[columnField] === true || visibleColumns[columnField] === undefined;
    let visibleColumnsCount = 0;

    settings.columnsOrderModel.forEach((item: string) => {
      if (visibleColumns[item] === true || visibleColumns[item] === undefined) {
        visibleColumnsCount += 1;
      }
    });

    const columnDef = columns.find((column) => column.field === columnField);

    if (!columnDef) return true;

    if (columnDef.hideable === false) {
      return true;
    }

    if (!isColumnVisible) {
      return false;
    }

    return visibleColumnsCount <= MIN_VISIBLE_COLUMNS;
  };

  const persistColumnWidths = useCallback(() => {
    const widths: Record<string, number> = {};

    for (const column of tableApi.current.getAllColumns()) {
      if (SERVICE_COLUMNS.includes(column.field)) {
        continue;
      }

      widths[column.field] = column.computedWidth ?? column.width ?? column.minWidth ?? 64;
    }

    displayManager.onWidthChange(widths);
  }, [displayManager, tableApi]);

  const handleCompressColumns = useCallback(() => {
    const widths: Record<string, number> = {};

    for (const column of tableApi.current.getAllColumns()) {
      if (SERVICE_COLUMNS.includes(column.field)) {
        continue;
      }

      widths[column.field] = column.minWidth ?? 64;
    }

    displayManager.onWidthChange(widths);
  }, [displayManager, tableApi]);

  const handleAutosizeColumns = useCallback(() => {
    const fields = tableApi.current
      .getAllColumns()
      .map((column) => column.field)
      .filter((field) => !SERVICE_COLUMNS.includes(field));

    void tableApi.current
      .autosizeColumns({
        columns: fields,
        includeHeaders: true,
        expand: false,
      })
      .then(() => {
        persistColumnWidths();
      });
  }, [persistColumnWidths, tableApi]);

  const renderItems = (renderedColumns: GridColDef[]): ReactNode => {
    return renderedColumns.map((column) => {
      if (HIDDEN_IN_SETTINGS_SIDEBAR_COLUMNS.includes(column.field)) {
        return null;
      }

      const title = t(getTrajectoryColumnHeaderTKey(column.field, useShortColumnHeaders));

      return (
        <DisplaySettingItem
          key={column.field}
          field={column.field}
          title={title}
          unpinColumn={handleUnpinClick}
          isVisibilitySwitcherDisabled={checkIsColumnVisibilityDisabled(column.field)}
          isVisible={
            displayManager.settings.columnsVisibility?.[column.field] === undefined
              ? true
              : displayManager.settings.columnsVisibility[column.field]
          }
          isPinned={displayManager.displaySettingPinnedColumns.includes(column.field)}
          onColumnVisibilityChange={handleColumnVisibilityChange}
        />
      );
    });
  };

  return (
    <>
      <div className={styles.tableSettingsHeader}>
        <Field
          label={t('Settings.numberDecimalPlaces')}
          orientation="horizontal"
          size="s"
          className={styles.decimalPlacesField}
        >
          <InputNumber
            value={numberDecimalPlaces}
            min={MIN_NUMBER_DECIMAL_PLACES}
            max={MAX_NUMBER_DECIMAL_PLACES}
            step={1}
            precision={0}
            onChange={(value) => {
              if (typeof value !== 'number' || Number.isNaN(value)) {
                return;
              }

              displayManager.onNumberDecimalPlacesChange(value);
            }}
          />
        </Field>
        <div className={styles.shortNamesToggle}>
          <Checkbox
            checked={useShortColumnHeaders}
            onChange={(checked) => displayManager.onUseShortColumnHeadersChange(checked)}
          >
            {t('Settings.useShortColumnHeaders')}
          </Checkbox>
        </div>
        <div className={styles.columnWidthActions}>
          <Button size="s" variant="secondary" onClick={handleCompressColumns}>
            {t('Settings.compressColumns')}
          </Button>
          <Button size="s" variant="secondary" onClick={handleAutosizeColumns}>
            {t('Settings.autosizeColumns')}
          </Button>
        </div>
        <div className={styles.divider} />
      </div>
      <Scrollbar>
        <div className={styles.list}>
          {renderItems(leftPinned)}
          <DndContext modifiers={[restrictToVerticalAxis, restrictToParentElement]} onDragEnd={handleDragEnd}>
            <SortableContext items={orderedColumns.map((col) => col.field)}>
              <div>{renderItems(free)}</div>
            </SortableContext>
          </DndContext>
          {renderItems(rightPinned)}
        </div>
      </Scrollbar>
    </>
  );
});
